import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * A selectable card in the storefront's neutral recipe: hairline border,
 * soft dark fill when chosen. Used for delivery mode and payment method at
 * checkout and for the address book cards.
 */
export default function OptionCard({
  selected,
  onSelect,
  icon,
  title,
  badge,
  description,
  disabled = false,
  children,
  className,
}: {
  selected: boolean
  onSelect: () => void
  icon?: ReactNode
  title: ReactNode
  badge?: string
  description?: ReactNode
  disabled?: boolean
  children?: ReactNode
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'w-full rounded-lg border p-3 text-left text-sm transition-colors',
        selected ? 'border-foreground/40 bg-foreground/5' : 'border-foreground/10 hover:border-foreground/25',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span className="flex items-center gap-2 font-bold">
        {icon}
        {title}
        {badge && (
          <span className="rounded-full border border-foreground/15 px-2 py-0.5 text-2xs font-semibold text-foreground/70">
            {badge}
          </span>
        )}
      </span>
      {description && <span className="mt-1.5 block text-xs text-foreground/60">{description}</span>}
      {children}
    </button>
  )
}
